import VideocamIcon from '@mui/icons-material/Videocam';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton, Badge, Popover, Box, Typography, Stack,
  Divider, Button, alpha, List, ListItem,
  ListItemText, ListItemAvatar, Avatar,
} from '@mui/material';
import {
  NotificationsOutlined, Article, Gavel, Event as EventIcon, AccountBalance, Person,
  AccountBalanceWallet, SmartToy as SmartToyIcon, BugReport as BugReportIcon,
  HomeWork, CheckCircle, Cancel, ErrorOutlineRounded, TrendingDownRounded, CampaignRounded,
  ChatBubbleOutlineRounded, HowToReg, Loyalty, EditNote, PersonSearch,
} from '@mui/icons-material';
import { useResolveChangeRequestMutation, useGetPendingChangeRequestsQuery } from '../../store/api/customersApi';
import { usePermissions } from '../../hooks/usePermissions';
import { useGetRecentBlogPostsQuery } from '../../store/api/blogApi';
import {
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  type NotificationType,
} from '../../store/api/notificationsApi';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { getCrmPalette } from '../../theme/brand';

const GOLDEN = getCrmPalette().brand.golden;
const BLOG_READ_KEY = 'imp_blog_read_ids';

function loadBlogReadIds(): Set<string> {
  try {
    const raw = localStorage.getItem(BLOG_READ_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveBlogReadIds(ids: Set<string>) {
  try {
    localStorage.setItem(BLOG_READ_KEY, JSON.stringify([...ids].slice(-300)));
  } catch {}
}

function timeAgo(dateStr: string) {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: es });
  } catch {
    return '';
  }
}

type UnifiedItem = {
  key:    string;
  id:     string;
  source: 'blog' | 'notif';
  type:   NotificationType | 'blog';
  title:  string;
  body:   string;
  link:   string;
  time:   string;
  createdAt: string;
  read:   boolean;
  scope?: string;
  meta?:  Record<string, unknown>;
};

const ICON_MAP: Record<UnifiedItem['type'], { icon: React.ReactNode; color: string }> = {
  blog:                { icon: <Article fontSize="small" />,        color: GOLDEN },
  auction_request:     { icon: <Gavel fontSize="small" />,          color: '#EF4444' },
  auction_participant: { icon: <Person fontSize="small" />,         color: '#EF4444' },
  auction_bid:         { icon: <Gavel fontSize="small" />,          color: '#EF4444' },
  property_visit:      { icon: <EventIcon fontSize="small" />,      color: '#60A5FA' },
  mortgage_request:             { icon: <AccountBalance fontSize="small" />,       color: '#3B82F6' },
  wallet_commission:            { icon: <AccountBalanceWallet fontSize="small" />, color: '#10B981' },
  wallet_transfer:              { icon: <AccountBalanceWallet fontSize="small" />, color: '#60A5FA' },
  wallet_withdrawal_requested:  { icon: <AccountBalanceWallet fontSize="small" />, color: '#FBBF24' },
  wallet_withdrawal_approved:   { icon: <AccountBalanceWallet fontSize="small" />, color: '#10B981' },
  wallet_withdrawal_rejected:   { icon: <AccountBalanceWallet fontSize="small" />, color: '#F87171' },
  live_session:                 { icon: <VideocamIcon fontSize="small" />,          color: '#A78BFA' },
  gustavo_pre_visit:            { icon: <SmartToyIcon fontSize="small" />,          color: '#FBBF24' },
  gustavo_post_visit:           { icon: <SmartToyIcon fontSize="small" />,          color: '#60A5FA' },
  bug_report_created:           { icon: <BugReportIcon fontSize="small" />,         color: '#F87171' },
  bug_report_updated:           { icon: <BugReportIcon fontSize="small" />,         color: '#34D399' },
  reservation_message:          { icon: <ChatBubbleOutlineRounded fontSize="small" />, color: GOLDEN },
  property_reserved_rent:       { icon: <HomeWork fontSize="small" />,              color: GOLDEN   },
  property_rent_confirmed:      { icon: <CheckCircle fontSize="small" />,           color: '#10B981' },
  property_rent_cancelled:      { icon: <Cancel fontSize="small" />,                color: '#F87171' },
  property_reserved_sale:       { icon: <HomeWork fontSize="small" />,              color: '#818cf8' },
  property_sale_confirmed:      { icon: <CheckCircle fontSize="small" />,           color: '#10B981' },
  property_sale_cancelled:      { icon: <Cancel fontSize="small" />,                color: '#F87171' },
  property_published:           { icon: <HomeWork fontSize="small" />,              color: '#34D399' },
  property_republish_reminder:  { icon: <CampaignRounded fontSize="small" />,        color: GOLDEN },
  customer_change_request:      { icon: <EditNote fontSize="small" />,              color: '#F59E0B' },
  customer_change_resolved:     { icon: <CheckCircle fontSize="small" />,           color: '#34D399' },
  customer_activity:            { icon: <PersonSearch fontSize="small" />,          color: '#60A5FA' },
  error_spike:                  { icon: <ErrorOutlineRounded fontSize="small" />,   color: '#EF4444' },
  error_traffic_drop:           { icon: <TrendingDownRounded fontSize="small" />,   color: '#F59E0B' },
  platform_announcement:        { icon: <CampaignRounded fontSize="small" />,       color: GOLDEN },
  blog_comment_reply:           { icon: <ChatBubbleOutlineRounded fontSize="small" />, color: '#60A5FA' },
  job_application_received:      { icon: <HowToReg fontSize="small" />,              color: '#3B82F6' },
  referral_application_received: { icon: <Loyalty fontSize="small" />,               color: GOLDEN },
  referral_reward_earned:        { icon: <Loyalty fontSize="small" />,               color: '#10B981' },
  generic:                      { icon: <NotificationsOutlined fontSize="small" />, color: GOLDEN },
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [readBlogIds, setReadBlogIds] = useState<Set<string>>(loadBlogReadIds);

  const { data: recentPosts = [] } = useGetRecentBlogPostsQuery();
  const { data: notifData } = useGetNotificationsQuery(undefined, {
    pollingInterval: 60_000, // refresca cada minuto
  });
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();
  const [resolveChangeRequest, { isLoading: resolvingChange }] = useResolveChangeRequestMutation();
  // reqId → 'approved' | 'rejected' resueltos en esta sesión (para reemplazar los botones).
  const [resolvedChanges, setResolvedChanges] = useState<Record<string, 'approved' | 'rejected'>>({});
  // Solicitudes que SIGUEN pendientes (según el server). Si una notificación apunta a una
  // solicitud que ya NO está acá, es que se resolvió antes/en otra sesión → sin botones.
  // Solo la piden los que pueden leer clientes (agente/office_admin dueños de cartera). Los
  // roles sin permiso de clientes (ej. Soporte/booster_support) NO la disparan → evita el 403.
  const { hasPermission } = usePermissions();
  const canReadCustomers = hasPermission('customers:read_own') || hasPermission('customers:read_office') || hasPermission('customers:read_all');
  const { data: pendingChanges = [], isSuccess: pendingLoaded } = useGetPendingChangeRequestsQuery(undefined, { skip: !canReadCustomers });
  const pendingChangeIds = useMemo(() => new Set(pendingChanges.map((r) => r._id)), [pendingChanges]);

  const handleResolveChange = async (item: UnifiedItem, approve: boolean) => {
    const reqId = item.meta?.changeRequestId as string | undefined;
    if (!reqId) return;
    try {
      await resolveChangeRequest({ reqId, approve }).unwrap();
      setResolvedChanges((prev) => ({ ...prev, [reqId]: approve ? 'approved' : 'rejected' }));
      if (item.source === 'notif' && !item.read) markRead(item.id).catch(() => {});
    } catch { /* el error se maneja con el estado de carga; reintentable */ }
  };

  const items = useMemo<UnifiedItem[]>(() => {
    const blogItems: UnifiedItem[] = recentPosts.map((p) => ({
      key:    `blog-${p._id}`,
      id:     p._id,
      source: 'blog',
      type:   'blog',
      title:  p.title,
      body:   p.excerpt ?? '',
      link:   `/blog/${p._id}`,
      time:   p.publishedAt ? timeAgo(p.publishedAt) : '',
      createdAt: p.publishedAt ?? new Date().toISOString(),
      read:   readBlogIds.has(p._id),
      scope:  p.officeSnapshot?.name ?? 'General',
    }));

    const notifItems: UnifiedItem[] = (notifData?.data ?? []).map((n) => ({
      key:    `notif-${n._id}`,
      id:     n._id,
      source: 'notif',
      type:   n.type,
      title:  n.title,
      body:   n.body,
      link:   n.link ?? '#',
      time:   timeAgo(n.createdAt),
      createdAt: n.createdAt,
      read:   !!n.readAt,
      meta:   n.meta,
    }));

    return [...notifItems, ...blogItems].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [recentPosts, notifData, readBlogIds]);

  const unreadCount = items.filter((n) => !n.read).length;

  // Snapshot de las notificaciones no leídas en el momento de abrir la campanita:
  // mantiene el resaltado visual durante ESTA apertura aunque el backend ya las haya
  // marcado leídas, así el badge baja al instante pero el agente igual ve cuáles eran
  // nuevas. Al cerrar y reabrir ya no aparecen resaltadas. (Joel)
  const [highlightKeys, setHighlightKeys] = useState<Set<string>>(new Set());

  const handleMarkAll = () => {
    const next = new Set([...readBlogIds, ...recentPosts.map((p) => p._id)]);
    setReadBlogIds(next);
    saveBlogReadIds(next);
    markAllRead().catch(() => {});
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    // Congelamos cuáles estaban sin leer para resaltarlas durante esta apertura…
    setHighlightKeys(new Set(items.filter((n) => !n.read).map((n) => n.key)));
    // …y las marcamos leídas de una (baja el badge sin tener que tocar cada una). (Joel)
    if (unreadCount > 0) handleMarkAll();
  };
  const handleClose = () => setAnchor(null);

  const handleClick = (item: UnifiedItem) => {
    if (item.source === 'blog') {
      setReadBlogIds((prev) => {
        const next = new Set([...prev, item.id]);
        saveBlogReadIds(next);
        return next;
      });
    } else if (!item.read) {
      markRead(item.id).catch(() => {});
    }
    handleClose();

    try {
      const url = new URL(item.link, window.location.origin);
      if (url.searchParams.get('gustavo') === '1') {
        const prompt = url.searchParams.get('prompt') ?? undefined;
        window.dispatchEvent(new CustomEvent('gustavo:open', { detail: { prompt } }));
        return;
      }
      // Normalizar URLs absolutas del mismo origen a rutas relativas para evitar recarga
      if (url.origin === window.location.origin) {
        navigate(url.pathname + url.search + url.hash);
        return;
      }
    } catch { /* link relativo sin protocolo — ignorar */ }

    navigate(item.link);
  };

  return (
    <>
      <IconButton
        aria-label="Notificaciones"
        onClick={handleOpen}
        size="small"
        sx={{
          color: anchor ? 'primary.main' : 'text.secondary',
          borderRadius: 1.5,
          border: `1px solid ${anchor ? alpha(GOLDEN, 0.4) : 'transparent'}`,
          bgcolor: anchor ? alpha(GOLDEN, 0.12) : 'transparent',
          '&:hover': { bgcolor: alpha(GOLDEN, 0.1), color: 'primary.main' },
          transition: 'all 0.18s',
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16, px: 0.5, fontWeight: 700, boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}` } }}
        >
          <NotificationsOutlined
            fontSize="small"
            sx={unreadCount > 0 ? {
              transformOrigin: '50% 0%',
              animation: 'bellSwing 3.2s ease-in-out infinite',
              '@keyframes bellSwing': {
                '0%, 60%, 100%': { transform: 'rotate(0deg)' },
                '5%':  { transform: 'rotate(14deg)' },
                '10%': { transform: 'rotate(-12deg)' },
                '15%': { transform: 'rotate(8deg)' },
                '20%': { transform: 'rotate(-6deg)' },
                '25%': { transform: 'rotate(3deg)' },
                '30%': { transform: 'rotate(0deg)' },
              },
            } : undefined}
          />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 380, mt: 1.25, borderRadius: 3,
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              boxShadow: '0 24px 60px -12px rgba(0,0,0,0.7)',
              border: `1px solid ${alpha(GOLDEN, 0.18)}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '1px',
                background: `linear-gradient(90deg, transparent, ${alpha(GOLDEN, 0.6)}, transparent)`,
                pointerEvents: 'none', zIndex: 1,
              },
            },
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
          <Typography fontWeight={700} fontSize={15}>Notificaciones</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAll} sx={{ fontSize: 12, py: 0.25 }}>
              Marcar todo leído
            </Button>
          )}
        </Stack>

        <Divider />

        {items.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Box sx={{
              width: 52, height: 52, borderRadius: '50%', mx: 'auto', mb: 1.5,
              display: 'grid', placeItems: 'center',
              background: `linear-gradient(135deg, ${alpha(GOLDEN, 0.16)}, ${alpha(GOLDEN, 0.04)})`,
              border: `1px solid ${alpha(GOLDEN, 0.25)}`,
              color: GOLDEN,
            }}>
              <NotificationsOutlined sx={{ fontSize: 26 }} />
            </Box>
            <Typography variant="body2" color="text.secondary">Sin notificaciones</Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {items.map((n, i) => {
              const conf = ICON_MAP[n.type] ?? ICON_MAP.generic;
              // "Nueva" = estaba sin leer al abrir la campanita (snapshot). Así el
              // resaltado no desaparece de golpe cuando el markAllRead invalida. (Joel)
              const isNew = highlightKeys.has(n.key);
              return (
                <Box key={n.key}>
                  <ListItem
                    alignItems="flex-start"
                    onClick={() => handleClick(n)}
                    sx={{
                      cursor: 'pointer', px: 2, py: 1.25, position: 'relative',
                      bgcolor: isNew ? alpha(conf.color, 0.07) : 'transparent',
                      '&:hover': { bgcolor: alpha(conf.color, 0.13) },
                      transition: 'background 0.15s',
                      ...(isNew ? {
                        '&::before': {
                          content: '""', position: 'absolute', left: 0, top: 8, bottom: 8,
                          width: 3, borderRadius: '0 3px 3px 0', bgcolor: conf.color,
                        },
                      } : {}),
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar sx={{
                        width: 36, height: 36,
                        bgcolor: conf.color,
                        color: '#fff',
                      }}>
                        {conf.icon}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography fontSize={13} fontWeight={isNew ? 700 : 500} noWrap sx={{ flex: 1, mr: 1 }}>
                            {n.title}
                          </Typography>
                          {isNew && (
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: conf.color, flexShrink: 0 }} />
                          )}
                        </Stack>
                      }
                      secondary={
                        <>
                          <Typography component="span" fontSize={12} color="text.secondary" display="block" sx={{
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {n.body}
                          </Typography>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.25}>
                            <Typography component="span" fontSize={11} color="text.disabled">
                              {n.time}
                            </Typography>
                            {n.scope && (
                              <Typography component="span" fontSize={11} sx={{ color: alpha(conf.color, 0.75) }}>
                                {n.scope}
                              </Typography>
                            )}
                          </Stack>

                          {/* Solicitud de cambio: botones Aprobar/Rechazar inline. (Nahomis) */}
                          {n.type === 'customer_change_request' && (() => {
                            const reqId = n.meta?.changeRequestId as string | undefined;
                            const resolved = reqId ? resolvedChanges[reqId] : undefined;
                            if (resolved) {
                              return (
                                <Typography component="span" fontSize={11} display="block" mt={0.75}
                                  sx={{ color: resolved === 'approved' ? '#34D399' : '#F87171', fontWeight: 700 }}>
                                  {resolved === 'approved' ? '✓ Cambio aprobado' : '✗ Cambio rechazado'}
                                </Typography>
                              );
                            }
                            // Ya resuelta antes / en otra sesión: no está entre las pendientes → sin botones.
                            if (pendingLoaded && reqId && !pendingChangeIds.has(reqId)) {
                              return (
                                <Typography component="span" fontSize={11} display="block" mt={0.75}
                                  sx={{ color: 'text.secondary', fontWeight: 700 }}>
                                  Solicitud ya resuelta
                                </Typography>
                              );
                            }
                            return (
                              <Stack direction="row" spacing={1} mt={0.75} onClick={(e) => e.stopPropagation()}>
                                <Button size="small" variant="contained" disabled={resolvingChange}
                                  onClick={() => handleResolveChange(n, true)}
                                  sx={{ fontSize: 11, py: 0.2, minWidth: 0, px: 1.5, bgcolor: '#34D399', color: '#0A0A0A', '&:hover': { bgcolor: '#10B981' } }}>
                                  Aprobar
                                </Button>
                                <Button size="small" variant="outlined" disabled={resolvingChange}
                                  onClick={() => handleResolveChange(n, false)}
                                  sx={{ fontSize: 11, py: 0.2, minWidth: 0, px: 1.5, color: '#F87171', borderColor: alpha('#F87171', 0.5), '&:hover': { borderColor: '#F87171', bgcolor: alpha('#F87171', 0.08) } }}>
                                  Rechazar
                                </Button>
                              </Stack>
                            );
                          })()}
                        </>
                      }
                    />
                  </ListItem>
                  {i < items.length - 1 && <Divider sx={{ opacity: 0.5 }} />}
                </Box>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
